/* Pure theme logic — no React, no DOM writes beyond the documented helpers.
   Kept separate from the provider so it can be unit-tested directly. */

export const THEME_STORAGE_KEY = 'tricorps360.theme';
export const THEME_MODES = ['light', 'dark', 'system'];
export const DEFAULT_MODE = 'system';

export const isValidMode = (mode) => THEME_MODES.includes(mode);

export function systemPrefersDark() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/** Which of light|dark should actually render for a given mode. */
export function resolveTheme(mode, prefersDark = systemPrefersDark()) {
  if (mode === 'light' || mode === 'dark') return mode;
  return prefersDark ? 'dark' : 'light';
}

export function readStoredMode() {
  let raw;
  try {
    raw = localStorage.getItem(THEME_STORAGE_KEY);
  } catch {
    return null; // unreadable storage — fall back to the default
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && isValidMode(parsed.mode)) return parsed;
  } catch {/* not JSON — may be a bare string from the pre-paint script */}
  if (isValidMode(raw)) return { mode: raw, updatedAt: 0 };
  return null;
}

export function writeStoredMode(mode, updatedAt = Date.now()) {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify({ mode, updatedAt }));
  } catch {/* private mode / quota — the in-memory state still works */}
}

/** Last write wins when the device and the server disagree. */
export function reconcileMode(local, server) {
  const localOk = local && isValidMode(local.mode);
  const serverOk = server && isValidMode(server.mode);
  if (!localOk && !serverOk) return { mode: DEFAULT_MODE, source: 'default' };
  if (!serverOk) return { mode: local.mode, source: 'local' };
  if (!localOk) return { mode: server.mode, source: 'server' };
  if (local.mode === server.mode) return { mode: local.mode, source: 'agree' };
  const localAt = Number(local.updatedAt) || 0;
  const serverAt = Number(server.updatedAt) || 0;
  return localAt >= serverAt
    ? { mode: local.mode, source: 'local' }
    : { mode: server.mode, source: 'server' };
}

/** Writes the resolved theme onto <html>. Safe to call repeatedly. */
export function applyTheme(resolved, { animate = false } = {}) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (animate) {
    root.setAttribute('data-theme-switching', '');
    window.setTimeout(() => root.removeAttribute('data-theme-switching'), 220);
  }
  root.setAttribute('data-theme', resolved);
  root.style.colorScheme = resolved;
}
